import { Box, Flex, FormControl, FormLabel, HStack, Text } from "@chakra-ui/react";
import parsePhoneNumberFromString from "libphonenumber-js";
import { Control, SubmitHandler, useForm } from "react-hook-form";

import { ContactDto, UserDto } from "@bcasmart/doxecure-commons/doxecureClientTypes";
import { LockedAccount } from "@bcasmart/doxecure-commons/doxecureTypes";
import { $length, $ok } from "foundation-ts/commons";
import { $trim } from "foundation-ts/strings";
import { Nullable } from "foundation-ts/types";

import { ErrorField } from "@components/ErrorField";
import { G1Button } from "@components/G1Button";
import { G1Input } from "@components/G1Input";
import { Phonenumber } from "@components/Phonenumber/Phonenumber";
import { SeaShellBox } from "@components/SeaShellBox";
import { config } from "@/config/config";
import { validateEmail } from "@utils/functions";
import { MandatoryEmail } from "@utils/TypesAndConstants";

interface ContactFormProps {
    mode: "create" | "edit";
    titleBtn?: string;
    blacklist?: string[];
    connectedUserProfile?: Nullable<UserDto>;
    lockedAccount?: LockedAccount;
    initialValues?: Partial<ContactDto>;
    onSubmit: SubmitHandler<NewPersonFormValues>;
    onDelete?: (contact?: ContactDto) => void;
}

export const ContactForm = (props: ContactFormProps) => {
    const { mode, initialValues, blacklist, connectedUserProfile, lockedAccount, titleBtn, onDelete, onSubmit } = props;
    const {
        control,
        handleSubmit,
        register,
        reset,
        formState: { errors },
    } = useForm<NewPersonFormValues>({
        mode: "onChange",
        reValidateMode: "onSubmit",
        defaultValues: { 
            email: initialValues?.contact?.email ?? "",
            lastName: initialValues?.contact?.lastName ?? "",
            firstName: initialValues?.contact?.firstName ?? "",
            phone: initialValues?.contact?.phone ?? ""
        },
    });
    const isEdit = mode === 'edit';
    const isDisabledDelButton = $ok(initialValues?.deletable) ? !initialValues!.deletable! : false;
    const emailsToBlackList: string[] = $trim(connectedUserProfile?.email).length !== 0 ? [$trim(connectedUserProfile?.email)] : [];
    if ($ok(blacklist)) { emailsToBlackList.push(...blacklist!); }

    const normalizePhone = (phone?: string): string | undefined => {
        if (!phone) { return undefined; }
        try {
            const phoneNumber = parsePhoneNumberFromString(phone);
            return $length(phoneNumber?.nationalNumber) > 0 ? phone : undefined;
        } catch {
            return undefined;
        }
    };

    const handleFormSubmit: SubmitHandler<NewPersonFormValues> = async (data) => {
        const success = await onSubmit({
            email: data.email.toLowerCase(),
            lastName: data.lastName.toUpperCase().trim(),
            firstName: data.firstName.trim().capitalize(),
            phone: normalizePhone(data.phone)
        });
        if (success) { reset(); }
    };

    return (
        <form noValidate onSubmit={handleSubmit(handleFormSubmit)}>
            <SeaShellBox component="form" spacing="5" mt={2}>
                <FormControl isRequired isDisabled={isEdit}>
                    <FormLabel>Adresse email</FormLabel>
                    <G1Input component="form" type="email" textTransform="lowercase" registerInput={{ ...register("email", { required: MandatoryEmail, validate: value => validateEmail(value, emailsToBlackList) }) }} />
                    {$ok(errors?.email?.message) && <ErrorField fieldName="email" error={`${errors!.email!.message!}`} />}
                    {isEdit && <Text fontSize="sm" pt={1} color="red">Si la mise à jour de l'email est nécessaire, alors nous vous conseillons de supprimer le contact et d'en créer un avec la nouvelle adresse.</Text>}
                </FormControl>
                <HStack align="start" spacing="4">
                    <FormControl isRequired>
                        <FormLabel>Nom</FormLabel>
                        <G1Input component="form" registerInput={{ ...register("lastName", { required: "Nom obligatoire", pattern: /^\s*[a-zàâäéèêëïîôöùûüç]+([ '-][a-zàâäéèêëïîôöùûüç]+)*\s*$/i }) }} textTransform="uppercase" />
                        <Box minH="20px">
                            {errors.lastName && <ErrorField fieldName="nom" error="Le 'Nom' est requis ou est invalide" />}
                        </Box>
                    </FormControl>
                    <FormControl>
                        <FormLabel>Prénom</FormLabel>
                        <G1Input component="form" registerInput={{ ...register("firstName", { required: "Prénom obligatoire", pattern: /^\s*[a-zàâäéèêëïîôöùûüç]+([ '-][a-zàâäéèêëïîôöùûüç]+)*\s*$/i }) }} textTransform="capitalize" />
                        <Box minH="20px">
                            {errors.firstName && <ErrorField fieldName="prénom" error="Le 'Prénom' est requis ou est invalide" />}
                        </Box>
                    </FormControl>
                </HStack>
                <FormControl>
                    <FormLabel>Numéro de téléphone portable</FormLabel>
                    <Phonenumber authorizedCountries={config.app.gsmAuthorizedCountries} height={40} control={control as unknown as Control} />
                    {errors.phone && <ErrorField fieldName="phone" error={errors.phone.message ?? "Le numéro de téléphone portable est invalide"} />}
                </FormControl>
            </SeaShellBox>
            {isEdit && isDisabledDelButton && (
                <SeaShellBox component="form" backgroundColor="orange" mt={2}>
                    <Text fontSize="sm">Ce contact étant défini dans des sessions en mode brouillon ou dans des modèles, vous ne pouvez pas le supprimer directement. Déréférencez-le de ces sessions et vous pourrez alors le supprimer de votre liste de contacts.</Text>
                </SeaShellBox>
            )}
            {isEdit && (
                <Flex alignItems="center" justifyContent='center' pt={3} gap={2}>
                    <G1Button component="form" variant="important" title="Modifier" type="submit" flex={1} height="50px" rounded="xl" fontSize="lg" isDisabled={$ok(errors.firstName) || $ok(errors.lastName) || $ok(errors.email) || $ok(errors.phone)} />
                    <G1Button component="form" variant="important" title="Supprimer" flex={1} height="50px" rounded="xl" fontSize="lg" isDisabled={isDisabledDelButton} onClick={() => onDelete?.(initialValues)} />
                </Flex>
            )}
            {!isEdit && (
                <G1Button component="form" variant="important" title={`Créer un ${titleBtn}`} type="submit" mt={3} width="100%" height="50px" rounded="xl" justifyContent="center" fontSize="lg"
                          disabled={lockedAccount?.exceedsUsersLimit || lockedAccount?.canceledAndWaitingSessionEnds} />
            )}
        </form>
    );
};

export type NewPersonFormValues = {
    email: string;
    lastName: string;
    firstName: string;
    phone?: string;
};